import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Plus, Users, TrendingUp, Pencil, Trash2 } from 'lucide-react';
import crm, { CAMPAIGN_TYPES, CAMPAIGN_STATUS } from '../crm/api';

const typeOf = (k) => CAMPAIGN_TYPES.find((t) => t.key === k) || CAMPAIGN_TYPES[6];
const statusOf = (k) => CAMPAIGN_STATUS.find((s) => s.key === k) || CAMPAIGN_STATUS[1];
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

const BLANK = { name: '', type: 'whatsapp', channel: '', status: 'active', ownerId: '', landingType: 'microsite', landingSlug: '', startDate: '', endDate: '', targetLeads: '', budget: '', description: '', scope: 'all', memberIds: [] };

export default function Campaigns() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = closed; object = create/edit

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([crm.get('/campaigns'), crm.get('/campaigns/reps')])
      .then(([c, r]) => { setList(c.data.campaigns || []); setReps(r.data.reps || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setForm({ ...BLANK });
  const openEdit = (c) => setForm({ ...BLANK, ...c, ownerId: c.owner?.id || '', startDate: c.startDate || '', endDate: c.endDate || '' });
  const save = async () => {
    if (!form.name.trim()) return;
    if (form.id) await crm.put(`/campaigns/${form.id}`, form);
    else await crm.post('/campaigns', form);
    setForm(null); load();
  };
  const del = async (c) => { if (window.confirm(`Delete campaign "${c.name}"? Its leads stay, but become Direct.`)) { await crm.delete(`/campaigns/${c.id}`); load(); } };

  return (
    <div className="space-y-5 max-w-[1180px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-[var(--text)] tracking-tight flex items-center gap-2"><Megaphone size={22} />Campaigns</h1>
          <p className="text-sm text-[var(--muted)]">Track leads by campaign — separately or alongside the rest.</p>
        </div>
        <button onClick={openNew} className="acc-bg text-white font-bold px-4 py-2.5 rounded-xl flex items-center gap-1.5"><Plus size={16} />New campaign</button>
      </div>

      {loading ? <div className="pt-10 text-[var(--muted)]">Loading…</div>
        : list.length === 0 ? (
          <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-12 text-center">
            <div className="text-4xl mb-2">📣</div>
            <div className="font-bold text-[var(--text)]">No campaigns yet</div>
            <div className="text-sm text-[var(--muted)] mt-1">Create one to start attributing leads.</div>
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))' }}>
            {list.map((c) => {
              const t = typeOf(c.type); const s = statusOf(c.status); const st = c.stats || {};
              return (
                <div key={c.id} onClick={() => nav(`/campaigns/${c.id}`)}
                  className="bg-[var(--card)] rounded-2xl border border-[var(--border)] card-lux p-5 cursor-pointer hover:-translate-y-0.5 transition-transform">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-extrabold text-[var(--text)] text-[15px] truncate">{c.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white" style={{ background: t.color }}>{t.label}</span>
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ color: s.color, background: s.color + '1a' }}>{s.label}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => openEdit(c)} className="w-7 h-7 rounded-lg flex items-center justify-center text-[var(--muted)] hover:bg-[var(--soft)]" title="Edit"><Pencil size={14} /></button>
                      <button onClick={() => del(c)} className="w-7 h-7 rounded-lg flex items-center justify-center text-[var(--muted)] hover:text-rose-500 hover:bg-[var(--soft)]" title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    <Stat icon={<Users size={13} />} label="Leads" value={st.leads || 0} />
                    <Stat icon={<TrendingUp size={13} />} label="Conv." value={(st.conversion || 0) + '%'} />
                    <Stat label="Pipeline" value={inr(st.pipelineValue)} small />
                  </div>
                  {c.targetLeads > 0 && (
                    <div className="mt-3">
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--soft)' }}>
                        <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round(((st.leads || 0) / c.targetLeads) * 100))}%`, background: 'linear-gradient(90deg,#10b981,#0b6e3e)' }} />
                      </div>
                      <div className="text-[10px] text-[var(--muted)] mt-1">{st.leads || 0} / {c.targetLeads} target</div>
                    </div>
                  )}
                  <div className="text-[11px] text-[var(--muted)] mt-3 flex items-center justify-between">
                    <span className="font-mono">?c={c.code}</span>
                    <span>{c.owner?.name || '—'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {form && <CampaignForm form={form} setForm={setForm} reps={reps} onSave={save} onClose={() => setForm(null)} />}
    </div>
  );
}

function Stat({ icon, label, value, small }) {
  return (
    <div className="rounded-xl p-2.5 text-center" style={{ background: 'var(--soft)' }}>
      <div className={`font-extrabold text-[var(--text)] ${small ? 'text-[13px]' : 'text-[18px]'} leading-none`}>{value}</div>
      <div className="text-[10px] text-[var(--muted)] mt-1 flex items-center justify-center gap-1">{icon}{label}</div>
    </div>
  );
}

function CampaignForm({ form, setForm, reps, onSave, onClose }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const inp = 'w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm bg-[var(--card)] text-[var(--text)] focus:ring-2 focus:ring-teal-500 focus:border-transparent';
  const lbl = 'text-[11px] uppercase text-[var(--muted)] font-semibold';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-[var(--card)] rounded-2xl shadow-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between sticky top-0 bg-[var(--card)]">
          <h2 className="font-extrabold text-[var(--text)]">{form.id ? 'Edit campaign' : 'New campaign'}</h2>
          <button onClick={onClose} className="text-[var(--muted)] text-2xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-3">
          <div><label className={lbl}>Name</label><input className={inp} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Diwali 2026 Clinic Outreach" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Type</label>
              <select className={inp} value={form.type} onChange={(e) => set('type', e.target.value)}>{CAMPAIGN_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></div>
            <div><label className={lbl}>Status</label>
              <select className={inp} value={form.status} onChange={(e) => set('status', e.target.value)}>{CAMPAIGN_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></div>
          </div>
          <div><label className={lbl}>Channel (optional)</label><input className={inp} value={form.channel || ''} onChange={(e) => set('channel', e.target.value)} placeholder="e.g. WhatsApp broadcast, Booth QR" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Owner (manager)</label>
              <select className={inp} value={form.ownerId || ''} onChange={(e) => set('ownerId', e.target.value)}><option value="">—</option>{reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></div>
            <div><label className={lbl}>Link opens</label>
              <select className={inp} value={form.landingType} onChange={(e) => set('landingType', e.target.value)}><option value="microsite">Microsite</option><option value="profile">Digital card</option></select></div>
          </div>
          <div><label className={lbl}>Landing rep (whose card/microsite)</label>
            <select className={inp} value={form.landingSlug || ''} onChange={(e) => set('landingSlug', e.target.value)}><option value="">—</option>{reps.map((r) => <option key={r.id} value={r.profileSlug}>{r.name}</option>)}</select></div>

          {/* Who this campaign auto-tags while it is running */}
          <div>
            <label className={lbl}>Tag leads captured by</label>
            <select className={inp} value={form.scope || 'all'} onChange={(e) => set('scope', e.target.value)}>
              <option value="all">Everyone on the team</option>
              <option value="members">Only the reps I pick</option>
            </select>
            <div className="text-[11.5px] text-[var(--muted)] mt-1.5">
              While this campaign is active and inside its dates, every lead these reps capture is tagged to it — no tracking link needed.
            </div>
          </div>
          {form.scope === 'members' && (
            <div>
              <label className={lbl}>Reps on this campaign</label>
              <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-auto p-2 rounded-lg border border-[var(--border)]" style={{ background: 'var(--soft)' }}>
                {reps.map((r) => {
                  const on = (form.memberIds || []).map(Number).includes(Number(r.id));
                  return (
                    <label key={r.id} className="flex items-center gap-2 text-[13px] cursor-pointer" style={{ color: 'var(--text)' }}>
                      <input type="checkbox" checked={on}
                        onChange={() => set('memberIds', on
                          ? (form.memberIds || []).filter((x) => Number(x) !== Number(r.id))
                          : [...(form.memberIds || []), r.id])} />
                      <span className="truncate">{r.name}</span>
                    </label>
                  );
                })}
              </div>
              {!(form.memberIds || []).length && (
                <div className="text-[11.5px] mt-1.5" style={{ color: '#b45309' }}>Pick at least one rep, or nothing will be tagged.</div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Start</label><input type="date" className={inp} value={form.startDate || ''} onChange={(e) => set('startDate', e.target.value)} /></div>
            <div><label className={lbl}>End</label><input type="date" className={inp} value={form.endDate || ''} onChange={(e) => set('endDate', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Target leads</label><input type="number" className={inp} value={form.targetLeads || ''} onChange={(e) => set('targetLeads', e.target.value)} placeholder="0" /></div>
            <div><label className={lbl}>Budget (₹)</label><input type="number" className={inp} value={form.budget || ''} onChange={(e) => set('budget', e.target.value)} placeholder="0" /></div>
          </div>
          <div><label className={lbl}>Notes</label><textarea rows={2} className={inp} value={form.description || ''} onChange={(e) => set('description', e.target.value)} /></div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end gap-2 sticky bottom-0 bg-[var(--card)]">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-[var(--muted)] border border-[var(--border)]">Cancel</button>
          <button onClick={onSave} className="acc-bg text-white px-4 py-2 rounded-lg text-sm font-bold">{form.id ? 'Save' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}
